import { cn } from '@/lib/utils';
import { useRuntimeConfig } from '@borradh-workspace/runtime-config/client';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { ExternalLink, MapPin } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

let optionsSet = false;

/*
 * Google does not THROW when it refuses a key (an unauthorised domain, such as
 * a preview URL, or a disabled API): the script loads, then it paints its own
 * grey "Oops! Something went wrong" box over the map and calls the global
 * `gm_authFailure` hook. Listen for that hook so every mounted map can swap to
 * the coordinates fallback instead of showing Google's error.
 */
let mapsAuthFailed = false;
const authFailureListeners = new Set<() => void>();
const listenForMapsAuthFailure = () => {
  const w = window as unknown as { gm_authFailure?: () => void };
  const previous = w.gm_authFailure;
  if ((previous as { __borradh?: boolean } | undefined)?.__borradh) return;
  const hook = () => {
    mapsAuthFailed = true;
    for (const listener of authFailureListeners) listener();
    previous?.();
  };
  (hook as { __borradh?: boolean }).__borradh = true;
  w.gm_authFailure = hook;
};
// Installed on import, i.e. before the editor can load any Maps script.
if (typeof window !== 'undefined') listenForMapsAuthFailure();

interface LocationPinMapProps {
  latitude: number;
  longitude: number;
  /**
   * Makes the pin draggable. Called with the new position when the owner
   * drops it — for nudging a pin Google put on the wrong side of a building.
   */
  onMove?: (position: { latitude: number; longitude: number }) => void;
  className?: string;
}

/** Google's own map for these coordinates — the no-key fallback links here. */
export const googleMapsUrlFor = (latitude: number, longitude: number) =>
  `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;

/**
 * A branch's map pin, so the owner can SEE where ads will be aimed before
 * saving (ENG-1302). Uses the same browser Maps key as the address search;
 * without one it degrades to the coordinates and a link to Google Maps rather
 * than a broken map.
 */
export function LocationPinMap({
  latitude,
  longitude,
  onMove,
  className,
}: LocationPinMapProps) {
  const { googleMapsApiKey } = useRuntimeConfig();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const errorObserverRef = useRef<MutationObserver | null>(null);
  const [failed, setFailed] = useState(mapsAuthFailed);

  useEffect(() => {
    const onAuthFailure = () => setFailed(true);
    authFailureListeners.add(onAuthFailure);
    return () => {
      authFailureListeners.delete(onAuthFailure);
    };
  }, []);

  // Same ref pattern as the address search: build the map ONCE and read the
  // latest callback from a ref, so an inline `onMove` doesn't rebuild it.
  const onMoveRef = useRef(onMove);
  useEffect(() => {
    onMoveRef.current = onMove;
  });
  const draggable = Boolean(onMove);

  // biome-ignore lint/correctness/useExhaustiveDependencies: built once per key; position updates are handled by the effect below
  useEffect(() => {
    if (!googleMapsApiKey) return;
    let cancelled = false;

    void (async () => {
      try {
        listenForMapsAuthFailure();
        if (!optionsSet) {
          setOptions({ key: googleMapsApiKey, v: 'weekly' });
          optionsSet = true;
        }
        await importLibrary('maps');
        await importLibrary('marker');
        if (cancelled || !containerRef.current) return;

        const position = { lat: latitude, lng: longitude };
        const map = new google.maps.Map(containerRef.current, {
          center: position,
          zoom: 16,
          disableDefaultUI: true,
          zoomControl: true,
          clickableIcons: false,
          gestureHandling: 'cooperative',
        });
        const marker = new google.maps.Marker({
          map,
          position,
          draggable,
        });
        marker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
          const moved = event.latLng;
          if (!moved) return;
          onMoveRef.current?.({
            latitude: moved.lat(),
            longitude: moved.lng(),
          });
        });
        mapRef.current = map;
        markerRef.current = marker;

        // If Google refused the key before this module could hook
        // `gm_authFailure`, its error box still lands in our container.
        const container = containerRef.current;
        const observer = new MutationObserver(() => {
          if (container.querySelector('.gm-err-container')) {
            observer.disconnect();
            setFailed(true);
          }
        });
        observer.observe(container, { childList: true, subtree: true });
        errorObserverRef.current = observer;
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      errorObserverRef.current?.disconnect();
      errorObserverRef.current = null;
      if (markerRef.current) {
        google.maps.event.clearInstanceListeners(markerRef.current);
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      mapRef.current = null;
    };
  }, [googleMapsApiKey]);

  // A new place picked, or a pin dragged: move the marker and recentre.
  useEffect(() => {
    const position = { lat: latitude, lng: longitude };
    markerRef.current?.setPosition(position);
    markerRef.current?.setDraggable(draggable);
    mapRef.current?.panTo(position);
  }, [latitude, longitude, draggable]);

  if (!googleMapsApiKey || failed) {
    return (
      <div
        className={cn(
          // Wraps rather than truncates: on a phone the link drops to its own
          // line so the coordinates — the thing being confirmed — stay whole.
          'flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-md border bg-muted/40 px-3 py-2 text-sm',
          className
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          <MapPin className="h-4 w-4 shrink-0 text-primary" />
          <span className="break-all tabular-nums">
            {latitude}, {longitude}
          </span>
        </span>
        <a
          href={googleMapsUrlFor(latitude, longitude)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex shrink-0 items-center gap-1 text-primary text-xs hover:underline"
        >
          Check on Google Maps
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    );
  }

  return (
    <div className={cn('space-y-1', className)}>
      <div
        ref={containerRef}
        role="img"
        aria-label={`Map pin at ${latitude}, ${longitude}`}
        className="h-48 w-full overflow-hidden rounded-md border"
      />
      {draggable ? (
        <p className="text-muted-foreground text-xs">
          Drag the pin if it isn’t quite on your door.
        </p>
      ) : null}
    </div>
  );
}
